import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, MapPin, Clock, Star, Users, Phone, Mail, ArrowRight, Menu, Calendar, Compass, Waves, Mountain, Camera, Building, Sunrise, X, CheckCircle, User, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import ToolSidebar from '../components/ToolSidebar';
import axios from 'axios';
import '../styles/TourPage.css';
import '../styles/HomePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const iconMap = { Adventure: Mountain, Sightseeing: Building, 'Water Sports': Waves, Cruise: Sunrise, 'Day Trip': Camera };

const TourPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const seo = useSEO('tours', {
    title: 'Dubai Tours | Desert Safari, Burj Khalifa, Jet Ski, City Tour — Book Online',
    description: 'Book Dubai tours at the best prices: Desert Safari with BBQ dinner, Burj Khalifa tickets, Jet Ski in Dubai Marina, Abu Dhabi day trip, city tour.',
    keywords: 'desert safari dubai, burj khalifa tickets, jet ski dubai marina, dubai city tour, abu dhabi tour from dubai'
  });
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingTour, setBookingTour] = useState(null);
  const [bookingForm, setBookingForm] = useState({ guest_name: '', number_of_people: 1, mobile: '', email: '', current_location: '', booking_date: '' });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const totalPrice = bookingTour?.price ? bookingTour.price * (bookingForm.number_of_people || 1) : 0;

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/tours`);
        if (Array.isArray(res.data)) {
          setTours(res.data);
        }
      } catch (e) {
        console.error('Error fetching tours:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  const allCategories = ['All', ...new Set(tours.map(t => t.category).filter(Boolean))];
  const filteredTours = activeFilter === 'All' ? tours : tours.filter(t => t.category === activeFilter);
  const getCategoryLabel = (cat) => cat === 'All' ? (t('tourAll') || 'All') : cat;

  const openBookingModal = (tour) => {
    setBookingForm({
      guest_name: isAuthenticated && user?.name ? user.name : '',
      number_of_people: 1,
      mobile: '',
      email: isAuthenticated && user?.email ? user.email : '',
      current_location: '',
      booking_date: ''
    });
    setBookingSuccess(false);
    setBookingTour(tour);
  };

  const submitBooking = async () => {
    if (!bookingForm.guest_name || !bookingForm.mobile) return;
    setBookingSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/tour-bookings`, {
        tour_title: bookingTour.title,
        tour_image: bookingTour.image || '',
        guest_name: bookingForm.guest_name,
        number_of_people: bookingForm.number_of_people,
        mobile: bookingForm.mobile,
        email: bookingForm.email,
        current_location: bookingForm.current_location,
        booking_date: bookingForm.booking_date,
        price_per_person: bookingTour.price || 0,
        total_price: totalPrice,
        user_email: isAuthenticated && user?.email ? user.email : bookingForm.email
      });
      setBookingSuccess(true);
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to submit booking. Please try again.');
    } finally {
      setBookingSubmitting(false);
    }
  };

  const whatsappLink = "https://wa.me/971569130707?text=Hi%20Registan%20Tourism%2C%20I%20want%20to%20book%20a%20tour";

  const tourStructuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Dubai Tours & Activities - Registan Tourism",
    "description": "Book Dubai tours including Desert Safari, Burj Khalifa tickets, Jet Ski, city tours, marina cruises and Abu Dhabi day trips.",
    "url": "https://registantours.com/tours",
    "address": { "@type": "PostalAddress", "addressLocality": "Dubai", "addressCountry": "AE" },
    "touristType": ["Adventure seekers", "Families", "Couples", "Solo travelers"],
    "availableLanguage": ["English", "Russian", "Arabic"]
  };

  return (
    <div className="tour-page">
      <Helmet>
        <link rel="canonical" href="https://registantours.com/tours" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/tours" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(tourStructuredData).replace(/</g, '\\u003c') }} />

      {/* Header */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/tours" className="dark-nav-link active">{t('tours') || 'Tours'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/tours" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <div className="tool-page-content">

      {/* Hero Section */}
      <section className="tour-hero" data-testid="tour-hero">
        <div className="tour-hero-overlay" />
        <div className="tour-hero-content">
          <span className="tour-hero-badge">{t('tourHeroBadge') || 'Dubai Tours & Activities'}</span>
          <h1>{t('tourHeroTitle') || 'Explore the Best of Dubai'}</h1>
          <p>{t('tourHeroDesc') || 'Desert safaris, Burj Khalifa tickets, jet ski adventures, city tours, dinner cruises and more — all at the best prices with Registan Tourism.'}</p>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="tour-hero-cta" data-testid="tour-whatsapp-btn">
            <Phone size={18} /> {t('tourBookViaWhatsApp') || 'Book via WhatsApp'}
          </a>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="tour-filters" data-testid="tour-filters">
        {allCategories.map(cat => (
          <button key={cat} className={`tour-filter-btn ${activeFilter === cat ? 'active' : ''}`}
            onClick={() => setActiveFilter(cat)} data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
            {getCategoryLabel(cat)}
          </button>
        ))}
      </section>

      {/* Tour Cards */}
      <section className="tour-grid" data-testid="tour-grid">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <p>{t('tourLoading') || 'Loading tours...'}</p>
          </div>
        ) : filteredTours.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <Compass size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>{t('tourNoTours') || 'No tours available'}</h3>
            <p>{t('tourCheckBack') || 'Check back soon for exciting Dubai tours!'}</p>
          </div>
        ) : (
        filteredTours.map(tour => {
          const Icon = iconMap[tour.category] || Compass;
          return (
            <article key={tour.id} className="tour-card" data-testid={`tour-card-${tour.id}`}>
              <div className="tour-card-image">
                <img src={tour.image} alt={tour.title} loading="lazy" />
                {tour.category && <span className="tour-card-category">{tour.category}</span>}
              </div>
              <div className="tour-card-body">
                <div className="tour-card-header">
                  <div className="tour-card-icon"><Icon size={20} /></div>
                  <h3>{tour.title}</h3>
                </div>
                <p className="tour-card-desc">{tour.description}</p>
                {tour.highlights && tour.highlights.length > 0 && (
                  <div className="tour-card-highlights">
                    {tour.highlights.slice(0, 4).map((h, i) => (
                      <span key={i} className="tour-highlight-tag">{h}</span>
                    ))}
                  </div>
                )}
                <div className="tour-card-footer">
                  <div className="tour-card-meta">
                    {tour.duration && <span className="tour-meta-item"><Clock size={14} /> {tour.duration}</span>}
                    {tour.price > 0 && <span className="tour-meta-item tour-price"><DollarSign size={14} /> {tour.price} AED</span>}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); openBookingModal(tour); }}
                    className="tour-book-btn" data-testid={`book-tour-${tour.id}`}>
                    {t('tourBookNow') || 'Book Now'} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </article>
          );
        })
        )}
      </section>

      {/* Why Book Section */}
      <section className="tour-why-section">
        <h2>{t('tourWhyTitle') || 'Why Book Dubai Tours with Registan Tourism?'}</h2>
        <div className="tour-why-grid">
          <div className="tour-why-card">
            <Star size={28} />
            <h4>{t('tourBestPrice') || 'Best Price Guarantee'}</h4>
            <p>{t('tourBestPriceDesc') || 'We offer the most competitive prices for all Dubai tours and activities. No hidden fees.'}</p>
          </div>
          <div className="tour-why-card">
            <Users size={28} />
            <h4>{t('tourExpertGuides') || 'Expert Local Guides'}</h4>
            <p>{t('tourExpertGuidesDesc') || 'All tours led by certified local guides who know Dubai inside out.'}</p>
          </div>
          <div className="tour-why-card">
            <Calendar size={28} />
            <h4>{t('tourFlexibleBooking') || 'Flexible Booking'}</h4>
            <p>{t('tourFlexibleBookingDesc') || 'Free cancellation up to 24 hours before. Book now, pay later option available.'}</p>
          </div>
          <div className="tour-why-card">
            <Phone size={28} />
            <h4>{t('tourWhatsAppSupport') || '24/7 WhatsApp Support'}</h4>
            <p>{t('tourWhatsAppSupportDesc') || 'Instant booking and support on WhatsApp. We respond within minutes.'}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="tour-cta-section">
        <h2>{t('tourReadyTitle') || 'Ready for Your Dubai Adventure?'}</h2>
        <p>{t('tourReadyDesc') || 'Contact us on WhatsApp for instant booking, custom tour packages, and group discounts.'}</p>
        <div className="tour-cta-buttons">
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="tour-cta-whatsapp">
            <Phone size={18} /> WhatsApp: +971 56 913 0707
          </a>
          <a href="mailto:booking@registantours.com" className="tour-cta-email">
            <Mail size={16} /> booking@registantours.com
          </a>
        </div>
      </section>

      {/* Tour Booking Modal */}
      {bookingTour && (
        <div className="tour-booking-overlay" onClick={(e) => { if (e.target.className === 'tour-booking-overlay') { setBookingTour(null); } }}>
          <div className="tour-booking-modal" data-testid="tour-booking-modal">
            {bookingSuccess ? (
              <div className="tour-booking-success">
                <CheckCircle size={56} />
                <h2>{t('tourBookingSubmitted') || 'Booking Submitted!'}</h2>
                <p>{t('tourBookingThankYou') || 'Your booking has been received. Our team will contact you shortly.'}</p>
                <button className="tour-booking-done-btn" onClick={() => setBookingTour(null)} data-testid="booking-done-btn">{t('tourDone') || 'Done'}</button>
              </div>
            ) : (
              <>
                <div className="tour-booking-header">
                  <div>
                    <h2>{t('tourBookTour') || 'Book Tour'}</h2>
                    <p className="tour-booking-tour-name">{bookingTour.title}</p>
                  </div>
                  <button className="tour-booking-close" onClick={() => setBookingTour(null)}><X size={22} /></button>
                </div>
                <div className="tour-booking-body">
                  <div className="tour-booking-field">
                    <label><User size={16} /> {t('tourGuestName') || 'Guest Name'} *</label>
                    <input type="text" value={bookingForm.guest_name} placeholder={t('tourFullName') || 'Full name'}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })} data-testid="booking-guest-name" />
                  </div>
                  <div className="tour-booking-field">
                    <label><Calendar size={16} /> {t('tourBookingDate') || 'Booking Date'} *</label>
                    <input type="date" value={bookingForm.booking_date}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setBookingForm({ ...bookingForm, booking_date: e.target.value })} data-testid="booking-date" />
                  </div>
                  <div className="tour-booking-field">
                    <label><Users size={16} /> {t('tourNumberOfPeople') || 'Number of People'} *</label>
                    <input type="number" min="1" value={bookingForm.number_of_people}
                      onChange={(e) => setBookingForm({ ...bookingForm, number_of_people: parseInt(e.target.value) || 1 })} data-testid="booking-people" />
                  </div>
                  <div className="tour-booking-field">
                    <label><Phone size={16} /> {t('tourMobileNumber') || 'Mobile Number'} *</label>
                    <input type="tel" value={bookingForm.mobile} placeholder="+971 XX XXX XXXX"
                      onChange={(e) => setBookingForm({ ...bookingForm, mobile: e.target.value })} data-testid="booking-mobile" />
                  </div>
                  <div className="tour-booking-field">
                    <label><Mail size={16} /> {t('tourEmailAddress') || 'Email Address'} *</label>
                    <input type="email" value={bookingForm.email} placeholder="your@email.com"
                      onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })} data-testid="booking-email"
                      readOnly={isAuthenticated && user?.email} 
                      style={isAuthenticated && user?.email ? { opacity: 0.7, cursor: 'default' } : {}} />
                  </div>
                  <div className="tour-booking-field">
                    <label><MapPin size={16} /> {t('tourCurrentLocation') || 'Current Location'}</label>
                    <input type="text" value={bookingForm.current_location} placeholder={t('tourHotelOrArea') || 'Hotel name or area'}
                      onChange={(e) => setBookingForm({ ...bookingForm, current_location: e.target.value })} data-testid="booking-location" />
                  </div>
                  {bookingTour.price > 0 && (
                    <div className="tour-booking-price-summary" data-testid="booking-price-summary">
                      <div className="price-row">
                        <span>{t('tourPricePerPerson') || 'Price per person'}</span>
                        <span>{bookingTour.price} AED</span>
                      </div>
                      <div className="price-row">
                        <span>{t('tourPeople') || 'People'}</span>
                        <span>x {bookingForm.number_of_people}</span>
                      </div>
                      <div className="price-row price-total">
                        <span>{t('tourTotal') || 'Total'}</span>
                        <span>{totalPrice} AED</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="tour-booking-footer">
                  <button className="tour-booking-cancel" onClick={() => setBookingTour(null)}>{t('tourCancel') || 'Cancel'}</button>
                  <button className="tour-booking-submit" onClick={submitBooking}
                    disabled={bookingSubmitting || !bookingForm.guest_name || !bookingForm.mobile} data-testid="booking-submit-btn">
                    {bookingSubmitting ? (t('tourSubmitting') || 'Submitting...') : (t('tourSubmitBooking') || 'Submit Booking')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <SiteFooter />
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default TourPage;
